// src/components/layout/Sidebar.jsx
import React from 'react';
import { NavLink, useLocation, Link } from 'react-router-dom'; 
import {
  HomeIcon as HomeIconOutline,
  Squares2X2Icon,
  DocumentChartBarIcon,
  QueueListIcon,
  TableCellsIcon,
  PlusCircleIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { HomeIcon as HomeIconSolid } from '@heroicons/react/24/solid';

// Social Media Icons 
import telegram from "../../assets/images/socials/telegram.svg";
import twitter from "../../assets/images/socials/twitter.svg";
import github from "../../assets/images/socials/github.svg";


const MainLogoIcon = ({ active = false }) => (
  <div
    className={`
      flex items-center justify-center h-11 w-11 rounded-full p-0.5 transition-all duration-200 group
      ${active
        ? 'bg-gradient-to-br from-blue-500 to-red-600 text-white shadow-lg shadow-blue-500/40 ring-2 ring-blue-400/80'
        : 'bg-slate-700 ring-1 ring-slate-600 hover:ring-blue-500 hover:bg-blue-500/20'
      }
    `}
  >
    {active ? (
      <HomeIconSolid className="h-6 w-6 text-white" />
    ) : (
      <HomeIconOutline className="h-6 w-6 text-slate-300 group-hover:text-blue-400" />
    )}
  </div>
);

const primaryNavItems = [
  { name: 'Dashboard', href: '/dashboard', icon: Squares2X2Icon },
  { name: 'Analytics', href: '/analytics', icon: DocumentChartBarIcon },
  { name: 'My Agents', href: '/my-agents', icon: QueueListIcon },
  // { name: 'Discover', href: '/discover', icon: TableCellsIcon },
];

const socialMedia = [
  // { name: 'Telegram', href: '#', icon: ({isActive}) => <img src={telegram} alt="Telegram" className={`w-8 h-8 p-2 rounded-lg ${isActive ? 'border-2 border-blue-500' : 'border border-slate-600'} flex items-center justify-center text-white text-sm font-bold`} /> },
  { name: 'Twitter', href: 'https://x.com/ServPylonAI', icon: ({isActive}) => <img src={twitter} alt="Twitter" className={`w-8 h-8 p-2 rounded-lg ${isActive ? 'border-2 border-blue-500' : 'border border-slate-600'} flex items-center justify-center text-white text-sm font-bold`} /> },
  // { name: 'GitHub', href: '#', icon: ({isActive}) => <img src={github} alt="GitHub" className={`w-8 h-8 p-2 rounded-lg ${isActive ? 'border-2 border-blue-500' : 'border border-slate-600'} flex items-center justify-center text-white text-sm font-bold`} /> },
 
];

const utilityIcons = [
    { name: 'Create New', actionKey: 'openCreateModal', icon: PlusCircleIcon, specialStyle: true },
];

// const bottomIcons = [
//     { name: 'Profile', href: '/profile', icon: UserCircleIcon },
// ];


const Sidebar = ({ onOpenCreateAgentModal }) => {
  const location = useLocation();
  const sidebarWidth = "w-[80px]";

  const handleUtilityClick = (actionKey) => {
    if (actionKey === 'openCreateModal' && onOpenCreateAgentModal) {
      onOpenCreateAgentModal();
    }
  };

  return (
    <aside
      className={`
        bg-slate-800/70 backdrop-blur-md
        text-slate-300
        flex flex-col items-center
        fixed top-16 left-3 bottom-3 
        z-40 
        border border-slate-700/50 
        ${sidebarWidth}
        rounded-2xl
        shadow-2xl shadow-black/40
        hidden lg:flex
        py-5 
      `}
    >
      <NavLink to="/" title="Home" end={true} className="mb-5 transition-transform hover:scale-110">
        <MainLogoIcon active={location.pathname === '/'} />
      </NavLink>

      <nav className="flex flex-col items-center space-y-3 w-full px-3">
        {primaryNavItems.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            title={item.name}
            className={({ isActive }) =>
              `flex items-center justify-center p-0 rounded-xl transition-all duration-150 ease-in-out w-12 h-12 group
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75
              ${isActive
                ? 'bg-blue-500/90 text-white shadow-md shadow-blue-500/30'
                : 'text-slate-400 hover:bg-slate-700 hover:text-slate-100'
              }`
            }
          >
            <item.icon className="h-6 w-6 flex-shrink-0" aria-hidden="true" />
            <span className="sr-only">{item.name}</span>
          </NavLink>
        ))}
      </nav>

      <hr className="border-slate-700 my-4 w-10 mx-auto" />

      <div className="flex flex-col items-center space-y-3 w-full px-3">
        {socialMedia.map((item) => (
          <Link 
            key={item.name}
            to={item.href}
            target="_blank"
            title={item.name}
            className="flex items-center justify-center rounded-xl transition-all duration-150 ease-in-out w-12 h-12 group hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white/50"
          >
            <item.icon isActive={location.pathname.startsWith(item.href)} />
            <span className="sr-only">{item.name}</span>
          </Link>
        ))}
      </div>

      <div className="flex-grow"></div>

       <div className="flex flex-col items-center space-y-3 w-full px-3 mb-3">
        {utilityIcons.map((item) => (
          <button 
            key={item.name}
            onClick={() => handleUtilityClick(item.actionKey)} 
            title={item.name}
            className={`
              flex items-center justify-center p-0 rounded-full transition-all duration-150 ease-in-out w-12 h-12 group
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75
              ${item.specialStyle 
                ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500 hover:text-white shadow-lg'
                : 'bg-slate-700/60 text-slate-300 hover:bg-slate-600 hover:text-slate-100'
              }
            `}
          >
            <item.icon className="h-7 w-7 flex-shrink-0" aria-hidden="true" />
            <span className="sr-only">{item.name}</span>
          </button>
        ))}
      </div>

      {/* <div className="flex flex-col items-center space-y-3 w-full px-3 pt-3 border-t border-slate-700">
        {bottomIcons.map((item) => (
          <NavLink 
            key={item.name}
            to={item.href}
            title={item.name}
            className={({ isActive }) =>
              `flex items-center justify-center p-0 rounded-full transition-all duration-150 ease-in-out w-10 h-10 group
              focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-opacity-75
              ${isActive
                ? 'bg-slate-600 text-white'
                : 'text-slate-400 hover:bg-slate-700 hover:text-slate-100'
              }`
            }
          >
            <item.icon className="h-6 w-6 flex-shrink-0" aria-hidden="true" />
            <span className="sr-only">{item.name}</span>
          </NavLink>
        ))}
      </div> */}
    </aside>
  );
};

export default Sidebar;